'use client';

import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
/**
 * Engineer impersonation provider (spec 048).
 *
 * Platform-neutral React component — no web/RN globals beyond React.
 * All platform-specific operations (sessionStorage hydration check,
 * toast, analytics) are injected via callback props so the provider
 * stays importable on both web and RN.
 *
 * On mount, calls `onHydrate` to validate a persisted grant token
 * with the backend. App-specific teardown (toast, telemetry) runs
 * inside `onHydrate` — not here. TTL monitoring starts when
 * `isActive` flips true (via the `startMonitor` prop).
 *
 * Relocated from `frontend/src/lib/impersonation/context.tsx` as part
 * of the auth cutover (spec 009, FILE 14, Group F).
 */
import { useEffect, useRef } from 'react';
// Module-level hydration guard. Page-load hydration must fire AT MOST
// once across the entire tab lifetime — not once per `ImpersonationProvider`
// mount. React Strict Mode double-invokes effects (mount → cleanup → mount),
// and Next.js may re-mount the layout-level provider on Fast Refresh.
// Hoisting the guard out of render scope guarantees a single call regardless
// of how many times the provider re-mounts.
let hydrationFired = false;
/** Reset the hydration guard. FOR TESTING ONLY — not part of the public API. */
export function _resetHydrationGuard() {
  hydrationFired = false;
}
export function ImpersonationProvider({
  children,
  isActive,
  onHydrate,
  startMonitor
}) {
  // Hold the latest `onHydrate` in a ref so the once-only hydration effect
  // can run with a stable (empty) dependency list without capturing a stale
  // closure — the guard below ensures it fires at most once per tab anyway.
  const onHydrateRef = useRef(onHydrate);
  onHydrateRef.current = onHydrate;
  useEffect(() => {
    if (hydrationFired) return;
    hydrationFired = true;
    onHydrateRef.current();
  }, []);
  // TTL monitor — wired to the live `isActive` flag so the timers
  // start when impersonation activates and clean up on teardown.
  useEffect(() => {
    if (!isActive) return undefined;
    const stop = startMonitor();
    return () => {
      stop();
    };
  }, [isActive, startMonitor]);
  return _jsx(_Fragment, {
    children: children
  });
}